import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from '../utils/api';

const PricingContext = createContext();

export const PricingProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem("selectedCurrency") || "USD";
  });

  const [prices, setPrices] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const [isAutoDetected, setIsAutoDetected] = useState(() => {
    return !localStorage.getItem("selectedCurrency");
  });
  // Hardcoded rates and mappings
  const hardCodedRates = {
    USD: 1,
    GBP: 0.80,
    EUR: 0.96,
    CAD: 1.44,
    AUD: 1.61,
    SAR: 3.75, // Added SAR with an exchange rate
  };

  const countryToCurrencyMap = {
    US: "USD",
    GB: "GBP",
    CA: "CAD",
    AU: "AUD",
    EU: "EUR", // Default for Europe
    SA: "SAR", // Added mapping for Saudi Arabia
  };


  const europeanContinents = ["EU"]; // European continent codes

  // Save the selected currency to localStorage
  useEffect(() => {
    localStorage.setItem("selectedCurrency", currency);
  }, [currency]);

  // Fetch prices for the selected currency
  const fetchPrices = useCallback(async () => {
    setIsFetching(true);
    try {
      // Fetch prices from API
      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/prices/currency/${currency}`);
      const pricesData = response.data;

      if (pricesData && pricesData.currency === currency) {
        setPrices({
          PS: parseFloat(pricesData.PS || 0),
          XB: parseFloat(pricesData.XB || 0),
          PC: parseFloat(pricesData.PC || 0),
        });
      } else {
        console.warn(`No prices found for currency: ${currency}`);
        setPrices({}); // Reset prices if no match is found
      }
    } catch (error) {
      console.error(`Error fetching prices for ${currency}:`, error);
      setPrices({}); // Reset prices on error
    } finally {
      setIsFetching(false);
    }
  }, [currency]);


  // Fetch prices whenever the currency changes
  useEffect(() => {
    fetchPrices();
  }, [currency, fetchPrices]);

  // Detect user location and set currency if not manually selected
  useEffect(() => {
    const fetchLocationAndSetCurrency = async () => {
      try {
        const response = await fetch("https://get.geojs.io/v1/ip/geo.json");
        const location = await response.json();
        const { country_code, continent_code } = location;

        let detectedCurrency;

        // Detect currency based on continent or country
        if (europeanContinents.includes(continent_code)) {
          detectedCurrency = "EUR";
        } else {
          detectedCurrency = countryToCurrencyMap[country_code] || "USD";
        }

        console.log("Detected currency:", detectedCurrency);

        if (isAutoDetected) {
          setCurrency(detectedCurrency);
        }
      } catch (error) {
        console.error("Error fetching location:", error);
        if (isAutoDetected) {
          setCurrency("USD");
        }
      }
    };

    if (isAutoDetected) {
      fetchLocationAndSetCurrency();
    }
  }, [isAutoDetected]);

  const updateCurrency = (newCurrency) => {
    localStorage.setItem("selectedCurrency", newCurrency);
    setCurrency(newCurrency);
    setIsAutoDetected(false); // Prevent auto-detection from overriding
  };


  return (
    <PricingContext.Provider
      value={{
        currency,
        setCurrency: updateCurrency,
        prices,
        isFetching,
        isAutoDetected,
        setIsAutoDetected,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => useContext(PricingContext);
